import * as React from "react";
import { Box, Dialog, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import Fullscreen from "@mui/icons-material/Fullscreen";
import FullscreenExit from "@mui/icons-material/FullscreenExit";
import CardBase from "../global/CardBase";
import SelectSimple from "../SelectSimple";
import HourLineChart from "../../features/dashboard/HourLineChart";

export interface ChartDataPoint {
  name: string;
  total: number;
  week: string;
}

interface ChartSectionProps {
  title: string;
  period: string;
  onPeriodChange: (event: SelectChangeEvent) => void;
  data: ChartDataPoint[];
  color: string;
  chartId: string;
}

const periodOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const ChartSection = ({
  title,
  period,
  onPeriodChange,
  data,
  color,
  chartId,
}: ChartSectionProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [fullscreenOpen, setFullscreenOpen] = React.useState(false);

  const handleOpenFullscreen = () => {
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
  };

  const chartContent = (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Box sx={{ minWidth: { xs: 300, sm: "100%" } }}>
        <HourLineChart id={fullscreenOpen ? `${chartId}-fullscreen` : chartId} data={data} color={color} />
      </Box>
    </Box>
  );

  return (
    <>
      <CardBase
        title={title}
        useGlassCard={true}
        headerActions={
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "flex-end", sm: "flex-start" },
            gap: 1,
            flexShrink: 0
          }}>
            <Box sx={{ minWidth: { xs: 100, sm: 120 } }}>
              <SelectSimple
                title="Period"
                data={periodOptions}
                value={period}
                onChange={onPeriodChange}
              />
            </Box>
            {isMobile && (
              <IconButton
                onClick={handleOpenFullscreen}
                size="small"
                sx={{
                  color: "text.secondary",
                  flexShrink: 0,
                  "&:hover": {
                    color: "text.primary",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Fullscreen />
              </IconButton>
            )}
          </Box>
        }
        sx={{
          minWidth: { xs: "100%", sm: 275 },
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: { xs: "none", sm: "translateY(-4px)" },
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 12px 40px rgba(0, 0, 0, 0.5)"
                : "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        {chartContent}
      </CardBase>

      {/* Fullscreen Dialog for Mobile */}
      <Dialog
        fullScreen
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        TransitionProps={{
          onEntered: () => {
            // Force chart resize after dialog opens
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
        }}
        sx={{
          "& .MuiDialog-paper": {
            background: theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(18, 18, 18, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 2,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <SelectSimple
                    title="Period"
                    data={periodOptions}
                    value={period}
                    onChange={onPeriodChange}
                  />
                </Box>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseFullscreen}
              sx={{
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <FullscreenExit />
            </IconButton>
          </Box>

          {/* Chart Content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Box sx={{ width: "100%", height: "100%", minHeight: 400 }}>
              <HourLineChart id={`${chartId}-fullscreen`} data={data} color={color} />
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ChartSection;

